import { createApi } from '@reduxjs/toolkit/query/react';

import { ResponseError } from '@/types/Error';
import {
  AddPaymentRequest,
  PaymentMethodInfo,
  PaymentMethodWithHyperWalletResponseInfo,
  UpdatePaymentRequest,
} from '@/types/PaymentMethod';
import { SuccessResponse } from '@/types/Success';

import { generateBaseQuseryWithReAuth } from '../baseQueryReauth';

export const paymentMethodsApi = createApi({
  reducerPath: 'paymentMethodsApi',
  keepUnusedDataFor: 300,
  baseQuery: generateBaseQuseryWithReAuth({ basePath: `account` }),
  tagTypes: ['PaymentMethods'],
  endpoints: builder => ({
    getAllPaymentMethods: builder.query<SuccessResponse<PaymentMethodInfo>, void>({
      query: () => ({
        url: '/GetAllPaymentMethods',
        method: 'GET',
      }),
      transformResponse: (response: SuccessResponse<PaymentMethodInfo>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
      providesTags: ['PaymentMethods'],
    }),
    getAllPaymentMethodsWithHyperWallet: builder.query<SuccessResponse<PaymentMethodWithHyperWalletResponseInfo>, void>(
      {
        query: () => ({
          url: '/GetAllPaymentMethodsWithHyperWallet',
          method: 'GET',
        }),
        transformResponse: (response: SuccessResponse<PaymentMethodWithHyperWalletResponseInfo>, meta, arg) => response,
        transformErrorResponse: response => response as ResponseError,
        providesTags: ['PaymentMethods'],
      }
    ),
    deletePaymentMethods: builder.mutation<SuccessResponse<unknown>, string | number>({
      query: id => ({
        url: `/DeleteCreditCard?type=${id}`,
        method: 'POST',
      }),
      transformResponse: (response: SuccessResponse<unknown>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
      invalidatesTags: ['PaymentMethods'],
    }),
    updatePaymentMethod: builder.mutation<SuccessResponse<unknown>, UpdatePaymentRequest>({
      query: payload => ({
        url: '/UpdateCreditCard',
        method: 'POST',
        body: payload,
      }),
      transformResponse: (response: SuccessResponse<unknown>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
      invalidatesTags: ['PaymentMethods'],
    }),
    addPaymentMethod: builder.mutation<SuccessResponse<unknown>, AddPaymentRequest>({
      query: payload => ({
        url: '/AddCreditCard',
        method: 'POST',
        body: payload,
      }),
      transformResponse: (response: SuccessResponse<unknown>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
      invalidatesTags: ['PaymentMethods'],
    }),
  }),
});

export const {
  useGetAllPaymentMethodsQuery,
  useGetAllPaymentMethodsWithHyperWalletQuery,
  useDeletePaymentMethodsMutation,
  useUpdatePaymentMethodMutation,
  useAddPaymentMethodMutation,
} = paymentMethodsApi;
